/**
 * Where the floating Raza AI button sits.
 *
 * Pure maths only — no DOM, no React — so the clamping can be tested without a
 * browser. The hook that drives the drag lives in hooks/useDraggableFab.
 */

export interface FabPoint {
    /** Distance from the LEFT edge of the viewport, in px. */
    x: number;
    /** Distance from the TOP edge, in px. */
    y: number;
}

export interface Viewport {
    width: number;
    height: number;
}

/** Breathing room kept between the button and the edge of the screen. */
export const FAB_MARGIN = 12;

/**
 * Keep the button on screen.
 *
 * Called on every drag frame AND on resize/rotate. Without the resize pass, a
 * button parked bottom-right on a desktop lands off-screen when the window
 * shrinks or a phone rotates — dragged out of reach with no way to get it back.
 */
export function clampToViewport(point: FabPoint, size: number, viewport: Viewport): FabPoint {
    // On a viewport narrower than the button itself, max < min; pin to the
    // margin rather than returning a negative coordinate.
    const maxX = Math.max(FAB_MARGIN, viewport.width - size - FAB_MARGIN);
    const maxY = Math.max(FAB_MARGIN, viewport.height - size - FAB_MARGIN);
    return {
        x: Math.min(Math.max(point.x, FAB_MARGIN), maxX),
        y: Math.min(Math.max(point.y, FAB_MARGIN), maxY),
    };
}

export type FabEdge = 'left' | 'right' | 'top' | 'bottom';

/** Which edge the button is closest to, measured from its centre. */
export function nearestEdge(point: FabPoint, size: number, viewport: Viewport): FabEdge {
    const centreX = point.x + size / 2;
    const centreY = point.y + size / 2;
    const distances: Record<FabEdge, number> = {
        left: centreX,
        right: viewport.width - centreX,
        top: centreY,
        bottom: viewport.height - centreY,
    };
    // Ties resolve in this order, which keeps the default corner (bottom-right)
    // stable instead of flipping between two equal edges.
    return (['right', 'bottom', 'left', 'top'] as FabEdge[])
        .reduce((best, edge) => (distances[edge] < distances[best] ? edge : best), 'right');
}

/**
 * Pin the button to the nearest edge, keeping how far along that edge it is.
 *
 * The button rests ON an edge, never floating in the middle of the screen —
 * the middle is where it covers the content you are trying to read. Dragging is
 * still free-form; this is applied on release, so it glides to the edge rather
 * than being dragged along a rail.
 */
export function snapToNearestEdge(point: FabPoint, size: number, viewport: Viewport): FabPoint {
    const clamped = clampToViewport(point, size, viewport);
    const edge = nearestEdge(clamped, size, viewport);
    switch (edge) {
        case 'left':   return { x: FAB_MARGIN, y: clamped.y };
        case 'right':  return { x: Math.max(FAB_MARGIN, viewport.width - size - FAB_MARGIN), y: clamped.y };
        case 'top':    return { x: clamped.x, y: FAB_MARGIN };
        case 'bottom': return { x: clamped.x, y: Math.max(FAB_MARGIN, viewport.height - size - FAB_MARGIN) };
    }
}

/**
 * Did the pointer travel far enough to count as a drag rather than a tap?
 *
 * Fingers wobble. Without a threshold, every tap registers a few pixels of
 * movement and the click that opens the chat gets swallowed as a drag.
 */
export const DRAG_THRESHOLD_PX = 4;

export function exceedsDragThreshold(from: FabPoint, to: FabPoint): boolean {
    return Math.hypot(to.x - from.x, to.y - from.y) > DRAG_THRESHOLD_PX;
}

/** Default resting place: bottom-right, matching the original CSS anchor. */
export function defaultPosition(size: number, viewport: Viewport, inset = 24): FabPoint {
    return clampToViewport(
        { x: viewport.width - size - inset, y: viewport.height - size - inset },
        size,
        viewport,
    );
}

export const FAB_STORAGE_KEY = 'raza_fab_position';

/** Parse a stored position, rejecting anything that isn't two finite numbers. */
export function parseStoredPosition(raw: string | null): FabPoint | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        const x = Number(parsed?.x);
        const y = Number(parsed?.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        return { x, y };
    } catch {
        // Corrupt entry: fall back to the default corner rather than throwing on
        // every render.
        return null;
    }
}

export interface PanelAnchor {
    /** Distance from the left edge of the viewport, px. */
    left: number;
    /** One of the two is set; the other is undefined. */
    top?: number;
    bottom?: number;
    /** For the open/close animation to grow out of the button. */
    transformOrigin: string;
}

/**
 * Where the chat panel sits, given where the button ended up.
 *
 * Centred on the button rather than pinned to a screen corner. Pinned, a button
 * dragged to the top-middle opened a panel over on the right with nothing
 * joining the two — it read as a different, unrelated thing appearing.
 *
 * Horizontally it is clamped so a button near an edge still gets a panel fully
 * on screen; vertically it opens away from whichever half the button is in, so
 * it never runs off the top or bottom.
 */
export function panelAnchor(
    point: FabPoint,
    size: number,
    viewport: Viewport,
    panelWidth: number,
    gap = 12,
): PanelAnchor {
    const width = Math.min(panelWidth, Math.max(0, viewport.width - FAB_MARGIN * 2));
    const centreX = point.x + size / 2;

    const maxLeft = Math.max(FAB_MARGIN, viewport.width - width - FAB_MARGIN);
    const left = Math.min(Math.max(centreX - width / 2, FAB_MARGIN), maxLeft);

    // Opens downward from the top half, upward from the bottom half — always
    // into the space that exists.
    const opensDown = point.y + size / 2 < viewport.height / 2;
    const originX = `${Math.min(Math.max(centreX - left, 0), width)}px`;

    return opensDown
        ? { left, top: point.y + size + gap, transformOrigin: `${originX} top` }
        : { left, bottom: Math.max(FAB_MARGIN, viewport.height - point.y + gap), transformOrigin: `${originX} bottom` };
}

/**
 * Which side of the screen the button is on.
 *
 * The chat panel opens from the button, so it has to know whether to grow left
 * or right — anchored right on a button dragged to the left edge, the panel
 * would run off the screen.
 */
export function panelSide(point: FabPoint, size: number, viewport: Viewport): 'left' | 'right' {
    return point.x + size / 2 < viewport.width / 2 ? 'left' : 'right';
}

/** Should the panel open upwards or downwards from the button? */
export function panelVertical(point: FabPoint, size: number, viewport: Viewport): 'up' | 'down' {
    return point.y + size / 2 > viewport.height / 2 ? 'up' : 'down';
}
